import type {
  FileResponseV2,
  GetV2WorkspacesWorkspaceidFilesQueryParamsSortEnumKey,
} from '@emma/api_client/types';
import { useCallback, useMemo } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import { UploadFile } from '@/components/file/upload/UploadFile';
import {
  buildFilterParams,
  type FilterConfig,
  serverTableParams,
} from '@/hooks/common/serverTableParams';
import { useFileDeleteHandler } from '@/hooks/file';
import {
  useAddWorkspaceFile,
  useCanWriteById,
  useDeleteWorkspaceFile,
  useUpdateWorkspaceFile,
  useWorkspaceFileDownload,
  useWorkspaceFiles,
} from '@/hooks/workspace';
import { fileTableColumns } from '../common/table/column_definitions/File';
import { TableStoreProvider, useTableStore } from '../common/table/store/Context';
import { Table } from '../common/table/Table';
import { FileContent } from './FileContent';

type Props = {
  workspaceId: number;
  actionOnErr?: () => void;
};

const FILTER_DEBOUNCE_MS = 300;
const DISABLED_ROW_OPACITY = 0.5;
const getRowProps = (file: FileResponseV2) =>
  file.isDisabled ? { opacity: DISABLED_ROW_OPACITY } : {};

const FILTERS: FilterConfig = {
  id: 'idIn',
  fileName: 'fileNameLike',
  label: 'labelLike',
  isDisabled: 'isDisabled',
  createdAt: ['createdAfter', 'createdBefore'],
  updatedAt: ['updatedAfter', 'updatedBefore'],
};

const FileTableContent = ({ workspaceId, actionOnErr }: Props) => {
  const canWrite = useCanWriteById(workspaceId);
  const { pagination, sorting, filters } = useTableStore((s) => s);
  const [debouncedFilters] = useDebounceValue(filters, FILTER_DEBOUNCE_MS);

  const queryParams = {
    ...serverTableParams<GetV2WorkspacesWorkspaceidFilesQueryParamsSortEnumKey>(
      pagination,
      sorting
    ),
    ...buildFilterParams(debouncedFilters, FILTERS),
  };

  const { data, isLoading, mutate, isValidating } = useWorkspaceFiles(workspaceId, queryParams);

  const totalItems = data?.data?.itemCount || 0;
  const pageCount = Math.ceil(totalItems / pagination.pageSize);

  const addFileMutation = useAddWorkspaceFile(workspaceId);
  const addFile = useCallback(
    async (data: { fileName: string; fileContents: string; label?: string }) => {
      const result = await addFileMutation({
        ...data,
        label: data.label || data.fileName,
      });
      await mutate();
      return result;
    },
    [addFileMutation, mutate]
  );

  const deleteFile = useDeleteWorkspaceFile(workspaceId, actionOnErr);
  const updateFile = useUpdateWorkspaceFile(workspaceId, actionOnErr);
  const handleDownload = useWorkspaceFileDownload(workspaceId, actionOnErr);
  const { deletingIds, handleDelete } = useFileDeleteHandler(deleteFile);

  const columns = useMemo(
    () =>
      fileTableColumns(
        canWrite ? handleDelete : undefined,
        handleDownload,
        canWrite ? updateFile : undefined
      ),
    [canWrite, handleDelete, handleDownload, updateFile]
  );

  const filteredFiles = useMemo(() => {
    const files = data?.data?.files || [];
    return files.filter((f) => !deletingIds.has(f.id));
  }, [data?.data?.files, deletingIds]) as FileResponseV2[];

  return (
    <>
      <Table<FileResponseV2>
        columns={columns}
        data={filteredFiles}
        expandableRow={({ original: { id } }) => (
          <FileContent fileId={id} workspaceId={workspaceId} />
        )}
        getRowProps={getRowProps}
        isLoading={isLoading}
        isValidating={isValidating}
        onRowClick={(_, row) => row.toggleExpanded()}
        refresh={mutate}
        serverSide={{ pageCount, rowCount: totalItems }}
        title="Files"
      />
      {canWrite && <UploadFile onUpload={addFile} />}
    </>
  );
};

export const FileTable = (props: Props) => (
  <TableStoreProvider
    initialSorting={[{ desc: true, id: 'id' }]}
    key={`WorkspaceFiles-${props.workspaceId}`}
    name={`WorkspaceFiles-${props.workspaceId}`}
  >
    <FileTableContent {...props} />
  </TableStoreProvider>
);
